import styled, { css } from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ACQ, ANIM, COLORS, MOBILE, REDUCED_MOTION, SPACE } from '@/nova/theme/tokens';

/** The track's inset around its tabs, as the Ads Budget modal draws it. */
const TRACK_INSET_PX = 6;
/** A tab's height: a comfortable tap target. */
const TAB_MIN_H_PX = 44;
/** The room under the strip, before the rail. */
const STRIP_GAP_PX = 12;
/** The height the strip takes above the split, its gap included: a split that fills the sheet's stage beside it
 *  (tabs at a desktop width, see EasySplit `$tabbed`) gives this much up. */
export const COMPOSER_TAB_STRIP_PX = TRACK_INSET_PX * 2 + TAB_MIN_H_PX + STRIP_GAP_PX;

const trackShown = css`
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: ${SPACE[4]};
  width: 100%;
  margin-bottom: ${STRIP_GAP_PX}px;
  padding: ${TRACK_INSET_PX}px;
  border-radius: 14px;
  background: ${ACQ.tabRow};
`;

/** An ad composer's tab strip: the Ads Budget modal's segmented tabs (a dark inset track holding equal-width tabs, the
 *  active one lifted with a subtle fill and bolder text). Pinned to the top of the sheet's scroll while the rail under
 *  it scrolls. On a phone always; wider only where the composer asks for it (`$always`: what it makes does not fit
 *  beside the rail), otherwise both rails show side by side. */
export const Track = styled.div<{ $always?: boolean }>`
  display: none;

  ${(p) => (p.$always ? trackShown : null)}

  ${MOBILE} {
    ${trackShown}
  }
`;

/** The active tab's fill, one pill under both tabs that glides to the picked one rather than jumping. Half the track
 *  inside its inset, less half the gap between the tabs; one tab and that gap along to sit under the second. */
export const Thumb = styled.span<{ $second: boolean }>`
  position: absolute;
  top: ${TRACK_INSET_PX}px;
  bottom: ${TRACK_INSET_PX}px;
  left: ${TRACK_INSET_PX}px;
  width: calc((100% - ${TRACK_INSET_PX * 2}px - ${SPACE[4]}) / 2);
  border-radius: 10px;
  background: ${ACQ.tabActiveBg};
  pointer-events: none;
  transform: ${(p) => (p.$second ? `translateX(calc(100% + ${SPACE[4]}))` : 'none')};
  transition: transform ${ANIM.COMPOSER_TAB_GLIDE_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  appearance: none;
  border: none;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${TAB_MIN_H_PX}px;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  color: ${(p) => (p.$active ? COLORS.heading : COLORS.textMuted)};
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: color ${ANIM.COMPOSER_TAB_GLIDE_MS}ms ${ANIM.EASE};
`;

export const TabLabel = styled(Text).attrs({ tag: 'span' as const })``;
